<!--
  Rodapé público — Figma "Site Desktop" > Footer (instância em Home, 1440x152).
  Linha: logo | nav | endereço; abaixo, os ícones sociais centralizados.
-->
<script setup lang="ts">
import { computed } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";

// as páginas legais recebem o idioma na URL (/use-terms/en); passamos o
// atual para quem navega em inglês ou espanhol abrir o documento no mesmo idioma
const { locale } = useI18n();
const legalLocale = computed(() =>
  ["pt", "en", "es"].includes(locale.value) ? locale.value : "pt"
);

const links = [
  { to: "/", label: "homeV2.footer.home" },
  { to: "/sobre-nos", label: "homeV2.footer.about" },
  { to: "/planos", label: "homeV2.footer.plans" },
  { to: "/contato", label: "homeV2.footer.contact" },
];
</script>

<template>
  <footer class="hv2 foot2">
    <div class="foot2__row">
      <RouterLink to="/" class="foot2__logo" aria-label="Selfie Check-in — início">
        <img src="/img/home/v2/logo-selfie.png" alt="Selfie Check-in" width="124" height="60" />
      </RouterLink>

      <nav class="foot2__nav">
        <RouterLink v-for="l in links" :key="l.to" :to="l.to" class="foot2__link">
          {{ $t(l.label) }}
        </RouterLink>
      </nav>

      <address class="foot2__address">
        {{ $t("homeV2.footer.addressLine1") }}<br />
        {{ $t("homeV2.footer.addressLine2") }}
      </address>
    </div>

    <div class="foot2__social">
      <a
        href="https://www.instagram.com/selfiecheckin/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Instagram"
      >
        <img src="/img/home/v2/icons/social-instagram.svg" alt="" width="24" height="24" />
      </a>
      <a
        href="https://www.linkedin.com/company/selfie-check-in/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="LinkedIn"
      >
        <img src="/img/home/v2/icons/social-linkedin.svg" alt="" width="24" height="24" />
      </a>
    </div>

    <nav class="foot2__legal">
      <RouterLink :to="`/use-terms/${legalLocale}`" class="foot2__legal-link">
        {{ $t("homeV2.footer.terms") }}
      </RouterLink>
      <span class="foot2__legal-sep" aria-hidden="true">·</span>
      <RouterLink :to="`/image-terms/${legalLocale}`" class="foot2__legal-link">
        {{ $t("homeV2.footer.imageTerms") }}
      </RouterLink>
    </nav>
  </footer>
</template>

<style scoped>
.foot2 {
  width: 100%;
  max-width: var(--hv2-max);
  margin-inline: auto;
  padding: 26px var(--hv2-gutter);
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.foot2__row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
}

.foot2__logo img {
  display: block;
  width: 124px;
  height: auto;
}

.foot2__nav {
  display: flex;
  align-items: center;
  gap: 32px;
}

.foot2__link {
  font-family: var(--hv2-font-ui);
  font-weight: 500;
  font-size: 16px;
  line-height: 23.2px;
  letter-spacing: -0.08px;
  color: rgba(0, 0, 0, 0.55);
  text-decoration: none;
  transition: color 0.18s ease;
}

.foot2__link:hover {
  color: var(--hv2-ink);
}

.foot2__address {
  /* o Figma usa 340px com Google Sans; a Figtree precisa de 341px e a linha
     do CNPJ quebrava em três. 360px dá folga sem mexer no resto da linha. */
  max-width: 360px;
  font-family: var(--hv2-font-display);
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: var(--hv2-ink);
}

.foot2__social {
  display: flex;
  align-items: center;
  gap: 24px;
}

.foot2__social img {
  display: block;
  width: 24px;
  height: 24px;
}

/* links legais: discretos, abaixo das redes, sem competir com a linha principal */
.foot2__legal {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px 12px;
}

.foot2__legal-link {
  font-family: var(--hv2-font-ui);
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.55);
  text-decoration: none;
  transition: color 0.18s ease;
}

.foot2__legal-link:hover {
  color: var(--hv2-ink);
  text-decoration: underline;
}

.foot2__legal-sep {
  color: rgba(0, 0, 0, 0.35);
  font-size: 13px;
}

@media (max-width: 900px) {
  .foot2 {
    padding: 32px var(--hv2-gutter);
    gap: 24px;
  }

  .foot2__row {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 20px;
  }

  .foot2__nav {
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
  }
}
</style>
